import axios, { isAxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Loader } from '@/components/core/Loader';
import { useFormatAttributeValue } from '@/components/datamodel/utils/formatAttributeValue';
import { DiscardChangesConfirmationModal } from '@/components/DiscardChangesConfirmationModal';
import { useDataModel } from '@/hooks/useDataModel';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { navigateTo } from '@/services/angularjs-bridge';
import type { PersistedItem } from '@/types/DataModel';

import {
    type AssetLabel,
    assetLabelIds,
    damAssetDownloadUrl,
    deleteAsset,
    linkItem,
    updateAsset,
    useAssetLinkedItems,
    useDamAsset,
    useLabels,
    useLinkedItemRecords,
    useReplaceAsset,
    useUnlinkItem,
} from './api';
import { createLabel, planLabelChanges, runLabelChanges } from './api/damLabels';
import { DamAssetDataPanel } from './components/DamAssetDataPanel';
import { DamAssetDetailHeader } from './components/DamAssetDetailHeader';
import { DamAssetHistoryPanel } from './components/DamAssetHistoryPanel';
import { DamAssetPreview } from './components/DamAssetPreview';
import { DamDeleteAssetsDialog } from './components/DamDeleteAssetsDialog';
import { DamImageLightbox } from './components/DamImageLightbox';
import { DamLibraryEmptyState } from './components/DamLibraryEmptyState';
import { DamLinkArticlesDialog } from './components/DamLinkArticlesDialog';
import { DamLinkedArticlesPanel } from './components/DamLinkedArticlesPanel';
import { DamManageTagsDialog } from './components/DamManageTagsDialog';
import { DamRemoveLinkDialog } from './components/DamRemoveLinkDialog';
import { DamReplaceImageDialog } from './components/DamReplaceImageDialog';
import { DAM_LIBRARY_PATH, parseDamAssetId } from './damRoutes';
import { DAM_DETAIL_LAYOUT, resolveDamDetailSections } from './utils/damDetailSections';
import { isBrowserRenderableImage, isImageMimeType } from './utils/damFileFormat';
import { useDamStatusBadges } from './utils/damStatusBadges';
import { sortLabelsAlphabetically } from './utils/damTags';
import { useDamLeaveGuard } from './utils/useDamLeaveGuard';
import { useDamLinkedItemColumns } from './utils/useDamLinkedItemColumns';
import { useRefetchOnPageRestore } from './utils/useRefetchOnPageRestore';

type DamDetailDialog =
    | { kind: 'lightbox' }
    | { kind: 'tags'; progress?: { done: number; count: number } }
    | { kind: 'link' }
    | { kind: 'unlink'; item: PersistedItem; isRemoving?: boolean }
    | { kind: 'replace'; progress?: number; errorMessage?: string }
    | { kind: 'delete'; isDeleting?: boolean };

/** Back from a detail page that was loaded on its own, e.g. through a direct link. */
const backToLibraryFromOwnPage = () => {
    // The referrer covers a detail page that was reloaded after it was opened from the library.
    const cameFromLibrary = document.referrer.startsWith(`${window.location.origin}${DAM_LIBRARY_PATH}`)
        && !document.referrer.startsWith(`${window.location.origin}${DAM_LIBRARY_PATH}/`);
    if (cameFromLibrary && window.history.length > 1) {
        window.history.back();
    } else {
        navigateTo(DAM_LIBRARY_PATH);
    }
};

interface DamAssetDetailPageProps {
    /** Asset shown when the library renders the page; otherwise it comes from the URL. */
    assetId?: string;
    /** Returns to the library that renders the page. */
    onBack?: () => void;
}

/**
 * Asset detail page `/products/assets/:assetId` (wireframe `6885:38450`, slot map
 * `dam-ui-rebuild-slot-map-detail.md`, D-043): header, preview with fullscreen and replace,
 * "Asset data" from `asset_detail` with tags, linked articles, history, delete. The library
 * renders it over itself; a direct link mounts it as a page of its own.
 */
function DamAssetDetailPage({ assetId, onBack }: DamAssetDetailPageProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { dataModel } = useDataModel();
    const { formatValue } = useFormatAttributeValue();
    const statusBadgesOf = useDamStatusBadges();
    const [primaryKey] = useState(() => assetId ?? parseDamAssetId(window.location.pathname) ?? '');
    const backToLibrary = onBack ?? backToLibraryFromOwnPage;
    const [dialog, setDialog] = useState<DamDetailDialog | null>(null);
    const [previewHeight, setPreviewHeight] = useState<number | undefined>(undefined);
    const isWide = useMediaQuery('(min-width: 1024px)');
    const [previewElement, setPreviewElement] = useState<HTMLDivElement | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const leaveGuard = useDamLeaveGuard(hasUnsavedChanges);
    const { leaveNow } = leaveGuard;
    const linkedRef = useRef<HTMLElement>(null);
    const historyRef = useRef<HTMLElement>(null);

    const { data: asset, error, isLoading, refetch } = useDamAsset({ variables: { primaryKey }, enabled: primaryKey !== '' });
    const { data: labels = [], refetch: refetchLabels } = useLabels();
    const { data: linked, refetch: refetchLinked } = useAssetLinkedItems({ variables: { primaryKey }, enabled: primaryKey !== '' });
    const linkedKeys = useMemo(() => linked?.itemPrimaryKeys ?? [], [linked]);
    const { data: linkedRecords } = useLinkedItemRecords({ variables: { primaryKeys: linkedKeys }, enabled: linkedKeys.length > 0 });
    const replaceMutation = useReplaceAsset();
    const unlinkMutation = useUnlinkItem();
    const linkedColumns = useDamLinkedItemColumns();

    const layout = dataModel?.assetLayout(DAM_DETAIL_LAYOUT);
    const sections = useMemo(() => resolveDamDetailSections(layout, dataModel), [layout, dataModel]);

    useEffect(() => {
        if (!previewElement) {
            return undefined;
        }
        const observer = new ResizeObserver(([entry]) => setPreviewHeight(entry.contentRect.height));
        observer.observe(previewElement);
        return () => observer.disconnect();
    }, [previewElement]);

    const linkedItems = useMemo(() => {
        if (linked === undefined) {
            return undefined;
        }
        if (linkedKeys.length === 0) {
            return [];
        }
        if (linkedRecords === undefined) {
            return undefined;
        }
        const byKey = new Map(linkedRecords.map((item) => [item.primaryKey__, item]));
        return linkedKeys.flatMap((key) => byKey.get(key) ?? []);
    }, [linked, linkedKeys, linkedRecords]);

    const describeItem = useCallback((item: PersistedItem) => {
        const values = item as unknown as Record<string, unknown>;
        const parts = [
            linkedColumns.titleOf?.(item),
            ...linkedColumns.columns.map((column) => formatValue(column, values[column.name])),
        ].filter((part): part is string => Boolean(part) && part !== '-' && part !== item.primaryKey__);
        return parts.join(' · ') || item.primaryKey__;
    }, [linkedColumns, formatValue]);

    const assignedLabels = useMemo(() => {
        const ids = new Set(assetLabelIds(asset?.tags__));
        return sortLabelsAlphabetically(labels.filter((label) => ids.has(label.id)));
    }, [asset?.tags__, labels]);

    const reloadAsset = useCallback(() => Promise.all([refetch(), refetchLinked()]), [refetch, refetchLinked]);
    useRefetchOnPageRestore(() => Promise.all([reloadAsset(), refetchLabels()]));

    const handleSave = useCallback(async (changed: Record<string, unknown>) => {
        try {
            await updateAsset({ primaryKey, category: asset?.category__, values: changed });
            toast.success(t('DAM.DETAIL.SAVE_DONE'));
            await refetch();
        } catch (saveError) {
            if (isAxiosError(saveError) && saveError.response?.status === 403) {
                toast.error(t('DAM.DETAIL.NOT_PERMITTED'));
            }
            throw saveError;
        }
    }, [primaryKey, asset?.category__, refetch, toast, t]);

    const handleDownload = useCallback(async () => {
        try {
            const response = await axios.get<Blob>(damAssetDownloadUrl(primaryKey), { responseType: 'blob' });
            const url = URL.createObjectURL(response.data);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = asset?.filename__ ?? primaryKey;
            anchor.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error(t('DAM.DETAIL.DOWNLOAD_FAILED'));
        }
    }, [primaryKey, asset?.filename__, toast, t]);

    const handleCreateLabel = useCallback(async (name: string): Promise<AssetLabel | undefined> => {
        try {
            const label = await createLabel(name);
            await refetchLabels();
            toast.success(t('DAM.TAGS.CREATED', { name: label.name }));
            return label;
        } catch {
            toast.error(t('DAM.TAGS.CREATE_FAILED'));
            return undefined;
        }
    }, [refetchLabels, toast, t]);

    const handleTagsConfirm = useCallback(async (added: string[], removed: string[]) => {
        const changes = planLabelChanges([{ primaryKey, labelIds: assetLabelIds(asset?.tags__) }], added, removed);
        setDialog({ kind: 'tags', progress: { done: 0, count: changes.length } });
        const result = await runLabelChanges(changes, (done) => setDialog({ kind: 'tags', progress: { done, count: changes.length } }));
        setDialog(null);
        if (result.failed.length === 0) {
            toast.success(t('DAM.DETAIL.TAGS_SAVED'));
        } else {
            toast.error(t('DAM.DETAIL.TAGS_FAILED'));
        }
        void refetch();
        void refetchLabels();
    }, [primaryKey, asset?.tags__, refetch, refetchLabels, toast, t]);

    const handleLink = useCallback(async (item: PersistedItem) => {
        try {
            await linkItem({ primaryKey, itemPrimaryKey: item.primaryKey__ });
            await reloadAsset();
        } catch {
            toast.error(t('DAM.DETAIL.LINK_FAILED'));
        }
    }, [primaryKey, reloadAsset, toast, t]);

    const handleUnlink = useCallback(async (item: PersistedItem) => {
        setDialog({ kind: 'unlink', item, isRemoving: true });
        try {
            await unlinkMutation.mutateAsync({ primaryKey, itemPrimaryKey: item.primaryKey__ });
            toast.success(t('DAM.DETAIL.LINK_REMOVED'));
            await reloadAsset();
        } catch {
            toast.error(t('DAM.DETAIL.UNLINK_FAILED'));
        } finally {
            setDialog(null);
        }
    }, [primaryKey, unlinkMutation, reloadAsset, toast, t]);

    const handleReplace = useCallback(async (file: File) => {
        setDialog({ kind: 'replace', progress: 0 });
        try {
            await replaceMutation.mutateAsync({
                primaryKey,
                file,
                onProgress: (percent) => setDialog({ kind: 'replace', progress: percent }),
            });
            setDialog(null);
            toast.success(t('DAM.DETAIL.REPLACE_DONE'));
            await reloadAsset();
        } catch (replaceError) {
            const status = isAxiosError(replaceError) ? replaceError.response?.status : undefined;
            const errorMessage = status === 409
                ? t('DAM.DETAIL.REPLACE_DUPLICATE')
                : status === 403 ? t('DAM.DETAIL.NOT_PERMITTED') : t('DAM.DETAIL.REPLACE_FAILED');
            setDialog({ kind: 'replace', errorMessage });
        }
    }, [primaryKey, replaceMutation, reloadAsset, toast, t]);

    const handleDelete = useCallback(async () => {
        setDialog({ kind: 'delete', isDeleting: true });
        try {
            await deleteAsset({ primaryKey });
            leaveNow(onBack ?? (() => navigateTo(DAM_LIBRARY_PATH)));
        } catch (deleteError) {
            setDialog(null);
            toast.error(isAxiosError(deleteError) && deleteError.response?.status === 403
                ? t('DAM.DETAIL.NOT_PERMITTED')
                : t('DAM.DETAIL.DELETE_FAILED'));
        }
    }, [primaryKey, leaveNow, onBack, toast, t]);

    const scrollTo = (element: HTMLElement | null) => {
        element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        element?.focus({ preventScroll: true });
    };

    if (isLoading || (primaryKey !== '' && !asset && !error)) {
        return (
            <div className="tw-flex tw-min-h-full tw-items-center tw-justify-center"><Loader /></div>
        );
    }

    if (!asset || !layout) {
        const status = isAxiosError(error) ? error.response?.status : undefined;
        const variant = !layout && asset ? 'error' : status === 403 ? 'noAccess' : status === 404 || primaryKey === '' ? 'notFound' : 'error';
        return (
            <main className="tw-flex tw-min-h-full tw-flex-col">
                <DamLibraryEmptyState variant={variant} onRetry={() => void refetch()} onBackToLibrary={backToLibrary} />
            </main>
        );
    }

    const filename = asset.filename__ ?? primaryKey;
    const isImage = isImageMimeType(asset.mimeType__);
    const imageUrl = typeof asset.publicUrl__ === 'string' && isBrowserRenderableImage(asset.mimeType__) ? asset.publicUrl__ : undefined;
    const badges = statusBadgesOf(asset, sections.HEADER_BADGES);
    const articleDescriptions = (linkedItems ?? []).map(describeItem);

    return (
        <div className="tw-flex tw-min-h-full tw-flex-col">
            <DamAssetDetailHeader
                title={filename}
                badges={badges}
                onBack={() => leaveGuard.requestLeave(backToLibrary)}
                onDownload={() => void handleDownload()}
                onDelete={() => setDialog({ kind: 'delete' })}
            />
            <main className="tw-flex tw-flex-col tw-gap-6 tw-px-4 tw-pb-10 md:tw-px-8 xl:tw-px-10">
                <section className="tw-flex tw-flex-col tw-gap-6 tw-rounded-lg tw-border tw-border-solid tw-border-tertiary tw-bg-fill-primary tw-p-4 md:tw-p-6 lg:tw-flex-row lg:tw-items-start">
                    <div ref={setPreviewElement} className="tw-min-w-0 lg:tw-flex-[3]">
                        <DamAssetPreview
                            imageUrl={imageUrl}
                            isImage={isImage}
                            title={filename}
                            onFullscreen={() => setDialog({ kind: 'lightbox' })}
                            onReplace={() => setDialog({ kind: 'replace' })}
                        />
                    </div>
                    <div className="tw-min-w-0 lg:tw-flex-[2]">
                        <DamAssetDataPanel
                            asset={asset}
                            attributes={sections.ASSET_DATA}
                            groups={sections.groups}
                            layout={layout}
                            tags={assignedLabels.map((label) => label.name)}
                            maxHeight={isWide ? previewHeight : undefined}
                            onSave={handleSave}
                            onManageTags={() => setDialog({ kind: 'tags' })}
                            onUnsavedChange={setHasUnsavedChanges}
                            onShowLinkedArticles={() => scrollTo(linkedRef.current)}
                            onShowHistory={() => scrollTo(historyRef.current)}
                        />
                    </div>
                </section>
                <DamLinkedArticlesPanel
                    ref={linkedRef}
                    items={linkedItems}
                    columns={linkedColumns.columns}
                    titleOf={linkedColumns.titleOf}
                    onAdd={() => setDialog({ kind: 'link' })}
                    onRemove={(item) => setDialog({ kind: 'unlink', item })}
                />
                <DamAssetHistoryPanel ref={historyRef} />
            </main>
            {dialog?.kind === 'lightbox' && imageUrl && (
                <DamImageLightbox imageUrl={imageUrl} title={filename} onClose={() => setDialog(null)} />
            )}
            {dialog?.kind === 'tags' && (
                <DamManageTagsDialog
                    count={1}
                    assets={[{ primaryKey, labelIds: assetLabelIds(asset.tags__) }]}
                    labels={labels}
                    onCreateLabel={handleCreateLabel}
                    onConfirm={(added, removed) => void handleTagsConfirm(added, removed)}
                    progress={dialog.progress}
                    onClose={() => setDialog(null)}
                />
            )}
            {dialog?.kind === 'link' && (
                <DamLinkArticlesDialog
                    describe={describeItem}
                    linkedKeys={new Set(linkedKeys)}
                    onLink={handleLink}
                    onClose={() => setDialog(null)}
                />
            )}
            {dialog?.kind === 'unlink' && (
                <DamRemoveLinkDialog
                    article={describeItem(dialog.item)}
                    isRemoving={Boolean(dialog.isRemoving)}
                    onConfirm={() => void handleUnlink(dialog.item)}
                    onClose={() => setDialog(null)}
                />
            )}
            {dialog?.kind === 'replace' && (
                <DamReplaceImageDialog
                    currentImageUrl={imageUrl}
                    currentFilename={filename}
                    isImage={isImage}
                    affectedArticles={articleDescriptions}
                    progress={dialog.progress}
                    errorMessage={dialog.errorMessage}
                    onConfirm={(file) => void handleReplace(file)}
                    onClose={() => setDialog(null)}
                />
            )}
            {dialog?.kind === 'delete' && (
                <DamDeleteAssetsDialog
                    count={1}
                    linkedArticles={articleDescriptions}
                    progress={dialog.isDeleting ? 0 : undefined}
                    onConfirm={() => void handleDelete()}
                    onClose={() => setDialog(null)}
                />
            )}
            <DiscardChangesConfirmationModal show={leaveGuard.isAsking} onDiscard={leaveGuard.discard} onClose={leaveGuard.stay} />
        </div>
    );
}

export { DamAssetDetailPage };
